import { z } from 'zod';

import { timestampSchema, uuidSchema } from '../general';
import { addressSchema, bigIntSchema, type Address, type StoredTransaction, type TransactionActionKey } from '../web3';

import { entityLabelSchema, entityTypeSchema, portfolioTradeTypeSchema } from './misc';
import { portfolioTypeSchema } from './portfolios';
import { pledgeOutcomeSchema } from './receipts';
import { stockTickerSchema } from './stocks';
import { evmTokenSchema, genericTokenSchema, otcTokenSchema } from './tokens';

export type TransactionAsActivity = Omit<StoredTransaction, 'failCount' | 'type'> & {
  transactionType: TransactionActionKey;
  type: 'transaction';
  occurredAtUtc: number;
  transactor: Address;
  transactionHash: Address;
  automated: false;
};
export type UserActivity = Activity | TransactionAsActivity;

export const activitySubjectSchema = z.enum([...entityTypeSchema.options, 'user', 'featured']);
export type ActivitySubject = z.infer<typeof activitySubjectSchema>;

const genericActivitySchema = z.object({
  id: uuidSchema,
  transactor: addressSchema,
  /** A timestamp */
  occurredAtUtc: timestampSchema,
  transactionHash: addressSchema.nullish(),
  type: z.string(),
  automated: z.boolean(),
  chainId: z.number(),
});
const valueTransferActivitySchema = genericActivitySchema.extend({
  // Can be either token amount or share amount
  amount: bigIntSchema,
  usdcAmount: bigIntSchema,
});

const onchainDonationActivitySchema = valueTransferActivitySchema.extend({
  transactor: z.string(), // Donations can have transactor as the donor's name
  type: z.literal('donation'),
  to: entityLabelSchema,
  token: z.union([genericTokenSchema, evmTokenSchema, otcTokenSchema]),
});

const basePledgeActivitySchema = genericActivitySchema.extend({
  transactor: addressSchema.nullish(),
  to: entityLabelSchema,
  pledgeId: uuidSchema,
  outcome: pledgeOutcomeSchema,
  chainId: z.number().nullish(),
});

const stockbasePledgeActivitySchema = basePledgeActivitySchema.extend({
  type: z.literal('stock_donation_pledge'),
  amount: z.number({ coerce: true }), // BE sends string but this will always be inside a number range
  stock: stockTickerSchema.extend({ id: uuidSchema }),
  usdcAmount: bigIntSchema,
});

const fiatbasePledgeActivitySchema = basePledgeActivitySchema.extend({
  type: z.literal('fiat_donation_pledge'),
  usdcAmount: bigIntSchema,
  selectedPaymentMethod: z.string().nullish(),
});

const custodianCashbasePledgeActivitySchema = basePledgeActivitySchema.extend({
  type: z.literal('custodian_cash_pledge'),
  usdcAmount: bigIntSchema,
});

const cashbasePledgeActivitySchema = basePledgeActivitySchema.extend({
  type: z.literal('cash_donation_pledge'),
  usdcAmount: bigIntSchema,
  cashTransferType: z.enum(['DafMigration', 'WirePledge']),
});

const cryptobasePledgeActivitySchema = basePledgeActivitySchema.extend({
  type: z.literal('crypto_donation_pledge'),
  amount: bigIntSchema,
  usdcAmount: bigIntSchema.nullish(),
  token: z.union([genericTokenSchema, otcTokenSchema, evmTokenSchema]),
});

const portfolioTradeOutcomeSchema = z.enum(['InTransit', 'Completed']);
export type PortfolioTradeOutcome = z.infer<typeof portfolioTradeOutcomeSchema>;

const allocationActivitySchema = genericActivitySchema.extend({
  type: z.literal('portfolio_trade'),
  fund: entityLabelSchema,
  tradeType: portfolioTradeTypeSchema,
  usdcAmount: bigIntSchema,
  portfolio: z.object({
    name: z.string(),
    id: uuidSchema,
    ticker: z.string().nullish(),
    type: portfolioTypeSchema,
  }),
  shares: bigIntSchema.nullish(),
  outcome: portfolioTradeOutcomeSchema,
});

const transferActivitySchema = valueTransferActivitySchema.extend({
  transactor: addressSchema.nullish(),
  type: z.enum(['grant', 'internal_transfer', 'pending_grant', 'pending_internal_transfer']),
  from: entityLabelSchema,
  to: entityLabelSchema,
  token: z.union([genericTokenSchema, evmTokenSchema, otcTokenSchema]),
});

export type OnchainDonationActivity = z.infer<typeof onchainDonationActivitySchema>;
export type StockPledgeActivity = z.infer<typeof stockbasePledgeActivitySchema>;
export type FiatPledgeActivity = z.infer<typeof fiatbasePledgeActivitySchema>;
export type CustodianCashPledgeActivity = z.infer<typeof custodianCashbasePledgeActivitySchema>;
export type CashPledgeActivity = z.infer<typeof cashbasePledgeActivitySchema>;
export type CryptoPledgeActivity = z.infer<typeof cryptobasePledgeActivitySchema>;
export type AllocationActivity = z.infer<typeof allocationActivitySchema>;
export type TransferActivity = z.infer<typeof transferActivitySchema>;

export const activitySchema = z.discriminatedUnion('type', [
  onchainDonationActivitySchema,
  stockbasePledgeActivitySchema,
  fiatbasePledgeActivitySchema,
  custodianCashbasePledgeActivitySchema,
  cashbasePledgeActivitySchema,
  cryptobasePledgeActivitySchema,
  allocationActivitySchema,
  transferActivitySchema,
]);
export type Activity = z.infer<typeof activitySchema>;

export const donationPledgeActivitySchema = z.union([
  stockbasePledgeActivitySchema,
  fiatbasePledgeActivitySchema,
  custodianCashbasePledgeActivitySchema,
  cashbasePledgeActivitySchema,
  cryptobasePledgeActivitySchema,
]);
export type DonationPledgeActivity = z.infer<typeof donationPledgeActivitySchema>;

export const paginatedActivityResponseSchema = z.object({
  items: z.array(activitySchema),
  count: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  returnedCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type PaginatedActivityResponse = z.infer<typeof paginatedActivityResponseSchema>;
